import type { TicketStatusV3Condition as TicketStatusV3ConditionType } from "@redotech/redo-model/advanced-flow/rules-v3/schemas";
import {
  CONVERSATION_STATUSES_V3,
  type ConversationStatusV3,
} from "@redotech/redo-model/advanced-flow/rules-v3/types";
import { RedoFilterDropdownAnchor } from "@redotech/redo-web/arbiter-components/filter-group/redo-filter-group";
import { RedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoMultiselectDropdown } from "@redotech/redo-web/arbiter-components/list/redo-multiselect-dropdown";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useMemo, useState } from "react";
import * as styles from "./conditions.module.css";

const STATUS_DISPLAY_NAMES: Record<ConversationStatusV3, string> = {
  OPEN: "Open",
  CLOSED: "Closed",
  SNOOZED: "Snoozed",
  IN_PROGRESS: "In Progress",
  HIDDEN: "Hidden",
  ARCHIVED: "Archived",
  DELETED: "Deleted",
};

const statusItems: RedoListItem<ConversationStatusV3>[] =
  CONVERSATION_STATUSES_V3.map((status) => ({
    id: status,
    type: "text" as const,
    text: STATUS_DISPLAY_NAMES[status],
    value: status,
  }));

export const TicketStatusV3Condition = memo(function TicketStatusV3Condition({
  condition,
  onChange,
  editableFields,
}: {
  condition: TicketStatusV3ConditionType;
  onChange: (updated: TicketStatusV3ConditionType) => void;
  editableFields?: ReadonlySet<string>;
}) {
  const statusesEditable =
    editableFields === undefined || editableFields.has("statuses");

  const [dropdownButtonRef, setDropdownButtonRef] =
    useState<HTMLButtonElement | null>(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const selectedItems = useMemo(() => {
    return (condition.statuses || []).map((status) => ({
      id: status,
      type: "text" as const,
      text: STATUS_DISPLAY_NAMES[status],
      value: status,
    }));
  }, [condition.statuses]);

  const displayText =
    (condition.statuses?.length || 0) > 0
      ? condition.statuses!.length === 1
        ? STATUS_DISPLAY_NAMES[condition.statuses![0]!]
        : `${condition.statuses!.length} statuses selected`
      : "Select statuses";

  return (
    <Flex dir="column" gap="sm" w="full">
      <Text color="secondary" fontSize="xs">
        Status
      </Text>
      <Flex
        style={
          statusesEditable ? undefined : { pointerEvents: "none", opacity: 0.6 }
        }
      >
        <RedoFilterDropdownAnchor
          className={styles.fullWidthButton}
          color="primary"
          onClick={() => statusesEditable && setDropdownOpen(!dropdownOpen)}
          ref={setDropdownButtonRef}
          text={displayText}
        />
      </Flex>
      <RedoMultiselectDropdown
        dropdownAnchor={dropdownButtonRef}
        dropdownOpen={dropdownOpen}
        fitToAnchor
        items={statusItems}
        selectedItems={selectedItems}
        selectionVariant="checkbox"
        setDropdownOpen={setDropdownOpen}
        setSelectedItems={(items) => {
          if (!statusesEditable) return;
          onChange({ ...condition, statuses: items.map((item) => item.value) });
        }}
        size="xs"
      />
    </Flex>
  );
});
